import { useReducer } from 'react';

import { type IAction } from 'src/utils/ts-utilities';

type IStep =
  | 'unpaid-invoices'
  | 'show-plan-usage-warning'
  | 'review-account-usage'
  | 'show-cancellation-prompt'
  | 'show-downgrade-confirmation'
  | 'plan-downgrade-in-progress'
  | 'discount-request'
  | 'cancellation-success'
  | 'keep-plan';

export type IBlockingApp = {
  id: string;
  name?: string | null;
  logoUrl?: string | null;
  unclaimed: boolean;
  activeProtectionsCount: number;
};

enum ActionEnum {
  SET_STEP = 'set-step',
  SET_BLOCKING_APPS = 'set-blocking-apps',
  SET_INITIAL_DATA = 'set-initial-data',
}

type IActionReducer =
  | IAction<ActionEnum.SET_INITIAL_DATA>
  | IAction<
      ActionEnum.SET_STEP,
      {
        step: IStep;
      }
    >
  | IAction<
      ActionEnum.SET_BLOCKING_APPS,
      {
        blockingApps: IBlockingApp[];
      }
    >;

type IState = {
  step: IStep | null;
  blockingApps: IBlockingApp[];
};

const initialState: IState = {
  step: null,
  blockingApps: [],
};

const reducer = (state: IState, action: IActionReducer): IState => {
  switch (action.type) {
    case ActionEnum.SET_STEP:
      return {
        ...state,
        step: action.payload.step,
      };

    case ActionEnum.SET_BLOCKING_APPS:
      return {
        ...state,
        blockingApps: action.payload.blockingApps,
      };

    case ActionEnum.SET_INITIAL_DATA:
      return initialState;
    default:
      return state;
  }
};

export const useCancellationPlanSlice = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setStep = (step: IStep) => dispatch({ type: ActionEnum.SET_STEP, payload: { step } });

  const setBlockingApps = (blockingApps: IBlockingApp[]) =>
    dispatch({ type: ActionEnum.SET_BLOCKING_APPS, payload: { blockingApps } });

  const setInitialData = () => dispatch({ type: ActionEnum.SET_INITIAL_DATA, payload: {} });

  return {
    ...state,
    setStep,
    setBlockingApps,
    setInitialData,
  };
};
